import React, { useEffect, useState } from 'react';
import { Grid, Card, CardContent, Typography, Container, Divider, Button, Box, Stack } from '@mui/material';
import Iconify from "../../components/iconify/Iconify";
import { Link } from "react-router-dom";
import { get } from '../../services/FetchHelper';
import EntityConstants from '../../configs/EntityConstants';
import RouteConstants from '../../configs/RouteConstants';

const StatCard = ({ title, value, icon, color }) => (
    <Card sx={{ height: '100%' }}>
        <CardContent>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Box>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        {title}
                    </Typography>
                    <Typography variant="h4" sx={{ color }}>
                        {value}
                    </Typography>
                </Box>
                <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: `${color}20` }}>
                    <Iconify icon={icon} width={32} sx={{ color }} />
                </Box>
            </Stack>
        </CardContent>
    </Card>
);

const QuickActionCard = ({ title, icon, to, color }) => (
    <Link to={to} style={{ textDecoration: 'none' }}>
        <Card sx={{ cursor: 'pointer', '&:hover': { boxShadow: 4 }, transition: 'box-shadow 0.2s' }}>
            <CardContent>
                <Stack direction="row" alignItems="center" spacing={2}>
                    <Box sx={{ p: 1, borderRadius: 1.5, bgcolor: `${color}20` }}>
                        <Iconify icon={icon} width={24} sx={{ color }} />
                    </Box>
                    <Typography variant="body1" fontWeight="medium">{title}</Typography>
                </Stack>
            </CardContent>
        </Card>
    </Link>
);

const Dashboard = () => {
    const [summary, setSummary] = useState({
        daily_collection: 0,
        total_outstanding: 0,
        active_riders: 0,
        schools_count: 0,
    });

    useEffect(() => {
        const fetchSummary = async () => {
            try {
                const response = await get(EntityConstants.DASHBOARD_SUMMARY);
                if (response.ok) {
                    const data = await response.json();
                    setSummary(data);
                }
            } catch (e) {
                // silent — dashboard is non-critical
            }
        };
        fetchSummary();
    }, []);

    const formatAmount = (val) => {
        const num = parseFloat(val) || 0;
        return `AED ${num.toLocaleString('en-AE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };

    const quickActions = [
        { title: "Students", icon: "mdi:account-school", to: RouteConstants.STUDENTS, color: "#2065d1" },
        { title: "Invoices", icon: "mdi:file-document-outline", to: RouteConstants.INVOICES, color: "#00a76f" },
        { title: "Receipts", icon: "mdi:receipt", to: RouteConstants.RECEIPTS, color: "#7635dc" },
        { title: "Daily Fee Collections", icon: "mdi:chart-bar", to: RouteConstants.DAILY_FEE_COLLECTION, color: "#ff5630" },
        { title: "Outstanding Fees", icon: "mdi:currency-usd-off", to: RouteConstants.OUTSTANDING_FEES, color: "#ff7849" },
        { title: "Missing Invoices", icon: "mdi:file-alert-outline", to: RouteConstants.MISSING_INVOICES, color: "#c8930a" },
    ];

    return (
        <Container maxWidth={false} sx={{ py: 3 }}>
            <Typography variant="h4" gutterBottom mb={3}>Dashboard</Typography>

            <Grid container spacing={3} mb={4}>
                <Grid item xs={12} sm={6} md={3}>
                    <StatCard
                        title="Today's Fee Collection"
                        value={formatAmount(summary.daily_collection)}
                        icon="mdi:cash-multiple"
                        color="#00a76f"
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <StatCard
                        title="Total Outstanding"
                        value={formatAmount(summary.total_outstanding)}
                        icon="mdi:currency-usd-off"
                        color="#ff5630"
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <StatCard
                        title="Active Riders"
                        value={summary.active_riders || 0}
                        icon="mdi:bus-school"
                        color="#2065d1"
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <StatCard
                        title="Schools"
                        value={summary.schools_count || 0}
                        icon="mdi:school-outline"
                        color="#7635dc"
                    />
                </Grid>
            </Grid>

            <Typography variant="h6" gutterBottom mb={2}>Quick Actions</Typography>
            <Grid container spacing={2}>
                {quickActions.map((action) => (
                    <Grid item xs={12} sm={6} md={4} key={action.to}>
                        <QuickActionCard {...action} />
                    </Grid>
                ))}
            </Grid>
        </Container>
    );
};

export default Dashboard;
